/**
 * Writes a delivery range such as "Delivered Tue, Oct 6 – Thu, Oct 8": min to max
 * business days (Saturday and Sunday skipped) after today or after data-start.
 * It works out the range again whenever its attributes change, which happens when
 * the availability line is re-rendered for another variant.
 */
class FaDeliveryEstimate extends HTMLElement {
  static observedAttributes = ['data-start', 'data-variant', 'data-min', 'data-max'];

  connectedCallback() {
    this.#render();
    if (/^\/(de|nl|fr|es|pt)(\/|$)/.test(window.location.pathname)) setTimeout(() => this.#render(), 1500);
  }

  attributeChangedCallback() {
    // A re-render morphs the attributes first and the text after, which would put the
    // server's fallback text back; working the range out once the morph is done keeps it
    if (this.isConnected) queueMicrotask(() => this.#render());
  }

  #render() {
    const min = Number(this.dataset.min) || 3;
    const max = Number(this.dataset.max) || 5;
    const start = this.#startDate();
    if (!start) return;

    // On a Weglot language folder (/de) the page is translated after Liquid ran, so this text,
    // written in the browser, would stay English: use the visitor's language for the dates and
    // the word before them.
    const weglotLang = currentWeglotLang();
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat(weglotLang || this.dataset.locale || undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      formatter = new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    }

    const from = formatter.format(addBusinessDays(start, min));
    const to = formatter.format(addBusinessDays(start, max));
    let prefixText = this.dataset.prefix || '';
    if (weglotLang && prefixText === 'Delivered' && DELIVERED[weglotLang]) prefixText = DELIVERED[weglotLang];
    const prefix = prefixText ? `${prefixText} ` : '';
    this.textContent = `${prefix}${from} – ${to}`;
  }

  /** @returns {Date | null} */
  #startDate() {
    const { start } = this.dataset;
    if (!start) return new Date();
    const [year, month, day] = start.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    // A ship date in the past says nothing about delivery; count from today instead
    const today = new Date();
    return date < today ? today : date;
  }
}

/** The word before the dates, per Weglot language */
const DELIVERED = { de: 'Lieferung', nl: 'Levering', fr: 'Livraison', es: 'Entrega', pt: 'Entrega' };

/**
 * The Weglot language when the page is a translated one (e.g. 'de'), else null.
 * @returns {string | null}
 */
function currentWeglotLang() {
  try {
    const weglot = /** @type {any} */ (window).Weglot;
    const lang = weglot && typeof weglot.getCurrentLang === 'function' ? weglot.getCurrentLang() : null;
    const from = weglot && weglot.options ? weglot.options.language_from : null;
    if (lang && from && lang !== from) return lang;
    // Weglot may not be ready on first render: fall back to the language folder in the path
    const match = window.location.pathname.match(/^\/(de|nl|fr|es|pt)(\/|$)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * @param {Date} date
 * @param {number} days
 * @returns {Date}
 */
function addBusinessDays(date, days) {
  const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const weekday = result.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return result;
}

if (!customElements.get('fa-delivery-estimate')) {
  customElements.define('fa-delivery-estimate', FaDeliveryEstimate);
}
